import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Category } from '../../models/Category'
import { Item } from '../../models/Item'
import { Result } from '../../types/utils'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { CategoryPage } from './CategoryPage'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { ImageView } from '../common/ImageView'
import { SelectList } from '../common/SelectList'

export type SelectableCategory = {
    category: Category
    selected: boolean
}

type DisplayCategoriesListProps = {
    categories: SelectableCategory[]
    componentItems: Item[]
    forOnboarding?: boolean
    onSave: (categories: Category[]) => Promise<Result<any>>
    allowNextWithoutSelection?: boolean
    onClose: () => void
    onBack?: {
        title?: string
        onClick?: () => void
    }
}

export function CategoriesPage({
    categories,
    onSave,
    componentItems,
    allowNextWithoutSelection,
    onBack,
    forOnboarding,
    onClose,
}: DisplayCategoriesListProps) {
    const navigate = useNavigate()

    const { logAndDisplayError } = useErrorHandler()
    const [categoryToEdit, setCategoryToEdit] = useState<
        { type: 'simple' | 'full'; category: Category } | undefined
    >(undefined)
    const [selectableCategories, setSelectableCategories] =
        useState<SelectableCategory[]>(categories)
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

    const selectableCategoryListItems = useMemo(() => {
        return selectableCategories.map((c) => ({
            id: c.category.id ?? '',
            title: c.category.name ?? '',
            subtitle:
                c.category.listings.length === 1
                    ? '1 service'
                    : `${c.category.listings.length} services`,
            tertiaryView: (
                <ImageView
                    fallbackIcon={{
                        icon: 'grid.large',
                        background: 'round',
                        className: 'text-primary-700',
                    }}
                    className="size-12 rounded-full"
                    image={c.category.image}
                />
            ),

            photo: undefined as File | undefined,
            selected: c.selected,
            button: c.selected
                ? {
                      title: 'Edit',
                      onClick: () => setCategoryToEdit({ type: 'simple', category: c.category }),
                  }
                : undefined,
        }))
    }, [selectableCategories])

    const [isSaving, setIsSaving] = useState(false)

    const onNextHandler = useCallback(async () => {
        // Send the new items with the proper IDs set
        setIsSaving(true)
        const result = await onSave(
            selectableCategories.filter((c) => c.selected).map((c) => c.category)
        )
        setIsSaving(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
        }
    }, [onSave, selectableCategories])

    return (
        <>
            <WorkflowPage
                backButtonTitle={onBack?.title}
                onBack={() => {
                    if (categoryToEdit?.type === 'full') {
                        setCategoryToEdit(undefined)
                    }
                    else {
                        onBack?.onClick?.()
                    }
                }}
                onClose={onClose}
                footerContent={
                    <div className="flex items-center justify-end">
                        <Button
                            intent="primary"
                            onClick={onNextHandler}
                            disabled={isSaving}
                            isLoading={isSaving}
                        >
                            Next
                        </Button>
                    </div>
                }
            >
                <Heading size="xl" className="mt-[5px]">
                    Categories
                </Heading>
                <Paragraph className="mt-4 max-w-[580px] text-center text-gray-600">
                    This is a set of suggested categories to organize your services and products.
                    Select and customize them for your business
                </Paragraph>
                <WorkflowPage.Card className="mt-12">
                    {selectableCategories.length ? (
                        <SelectList
                            selectionType="multi"
                            items={selectableCategoryListItems}
                            selectedItems={selectableCategoryListItems.filter((i) => i.selected)}
                            onSelectItems={(selectedCategories) => {
                                const selectedCategoryIDs = selectedCategories.map((c) => c.id)
                                setSelectableCategories((c) =>
                                    c.map((c) => {
                                        if (selectedCategoryIDs.includes(c.category.id ?? '')) {
                                            return { ...c, selected: true }
                                        }
                                        return { ...c, selected: false }
                                    })
                                )
                            }}
                        />
                    ) : (
                        <Paragraph size="md" className="text-center text-gray-600">
                            No categories found
                        </Paragraph>
                    )}
                </WorkflowPage.Card>
                {categoryToEdit?.type === 'simple' && (
                    <WorkflowPage.CategoryLiteModal
                        isOpen
                        title={`Edit category`}
                        onClose={() => setCategoryToEdit(undefined)}
                        category={categoryToEdit.category}
                        onDone={(updatedCategory) => {
                            setHasUnsavedChanges(true)
                            setSelectableCategories((s) =>
                                s.map((c) =>
                                    c.category.id === categoryToEdit.category.id
                                        ? { ...c, category: updatedCategory }
                                        : c
                                )
                            )
                            setCategoryToEdit(undefined)
                        }}
                        shouldShowPublicField={true}
                        onMoreDetails={() => setCategoryToEdit({ ...categoryToEdit, type: 'full' })}
                    />
                )}

                {categoryToEdit?.type === 'full' && (
                    <CategoryPage
                        category={categoryToEdit.category}
                        onSave={(updatedCategory, photo) => {
                            setHasUnsavedChanges(true)
                            setSelectableCategories((s) =>
                                s.map((c) =>
                                    c.category.id === categoryToEdit.category.id
                                        ? { ...c, category: updatedCategory, photo }
                                        : c
                                )
                            )
                            setCategoryToEdit(undefined)
                        }}
                        onClose={() => setCategoryToEdit(undefined)}
                        componentItems={componentItems}
                        enableAllFields={!forOnboarding}
                    />
                )}
            </WorkflowPage>
            <NavigationBlockerPrompt
                when={hasUnsavedChanges && !isSaving}
                allowCurrentPaths={[/onboarding\/.*/]}
            />
        </>
    )
}
